import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Send, Star, X, Menu, Settings, Paperclip } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ApiClient from '../utils/api';
import ChatSidebar from '../components/ChatSidebar';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import WaterGridBackground from '../components/WaterGridBackground';
import { useToast } from '../context/ToastContext';

const cleanErrorMessage = (text) => {
  if (!text) return text;
  const lower = text.toLowerCase();
  if (lower.includes("exceeded your current quota") || lower.includes("limit exceeded") || lower.includes("rate limit")) {
    return "Model limit exceeded. Please try again after a moment.";
  }
  if (lower.includes("api key") || lower.includes("apikey") || lower.includes("sk-proj") || lower.includes("invalid api key")) {
    return "API key configuration error. Please verify the API key under settings.";
  }
  return text;
};

const ChatInterface = ({onLogout, onAdminPanel, user, activeAiAgentId, aiAgents }) => {
  const { showToast } = useToast();
  const [messages, setMessages] = useState([]);
  const displayName = (() => {
    if (user?.username) return user.username;
    if (user?.name) return user.name;
    try {
      const stored = sessionStorage.getItem('user') || localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.username) return parsed.username;
        if (parsed?.name) return parsed.name;
      }
    } catch (_) {}
    return 'Umaya';
  })();
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstSubmitting, setIsFirstSubmitting] = useState(false);
  const centerAnchorRef = useRef(null);
  const dockRef = useRef(null);
  const [dockOffset, setDockOffset] = useState(-260);

  useLayoutEffect(() => {
    const updateOffset = () => {
      if (messages.length === 0 && centerAnchorRef.current && dockRef.current) {
        const anchorRect = centerAnchorRef.current.getBoundingClientRect();
        const dockRect = dockRef.current.getBoundingClientRect();
        const currentTransform = dockRef.current.style.transform || '';
        const match = currentTransform.match(/translateY\((-?\d+(\.\d+)?)px\)/);
        const currentY = match ? parseFloat(match[1]) : 0;
        const baseDockTop = dockRect.top - currentY;
        const rawOffset = anchorRect.top - baseDockTop;
        if (Math.abs(rawOffset) > 10) {
          setDockOffset(Math.round(rawOffset));
        }
      }
    };

    updateOffset();
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, [messages.length, historyLoaded]);
  const [sessionId, setSessionId] = useState(() => {
    const stored = localStorage.getItem('chat_interface_session_id');
    // Only restore if it's a real Agno-generated session ID (not "None"/"null"/etc.)
    if (stored && stored !== 'None' && stored !== 'null' && stored !== 'undefined') return stored;
    const initialId = crypto.randomUUID();
    localStorage.setItem('chat_interface_session_id', initialId);
    return initialId;
  });
  const [logoUrl, setLogoUrl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [agentStatus, setAgentStatus] = useState(null);
  const [sessionStatus, setSessionStatus] = useState('open');
  const [coords, setCoords] = useState(null);

  // Auto-resize input textarea to grow with content up to 200px then scroll
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      const newHeight = Math.min(el.scrollHeight, 220);
      el.style.height = `${Math.max(newHeight, 28)}px`;
    }
  }, [inputMessage]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.warn("Geolocation access denied or failed:", error);
        }
      );
    }
  }, []);

  // Prevent dragging text, elements, or ghost content from the chat workspace into other tabs
  useEffect(() => {
    const handleDragStart = (e) => {
      if (e.target && e.target.closest && (e.target.closest('.copilot-messages-container') || e.target.closest('.copilot-input-dock'))) {
        e.preventDefault();
      }
    };
    document.addEventListener('dragstart', handleDragStart);
    return () => document.removeEventListener('dragstart', handleDragStart);
  }, []);

  // Feedback Modal State
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  // Session Sidebar State
  const [sessions, setSessions] = useState([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsSidebarCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [learningEnabled] = useState(() => {
    const saved = localStorage.getItem('learning_enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [streamingEnabled, setStreamingEnabled] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [isSessionsLoading, setIsSessionsLoading] = useState(false);
  const SESSIONS_PER_PAGE = 10;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);

  // Botflow State
  const [responseMode, setResponseMode] = useState('ai');
  const [activeButtons, setActiveButtons] = useState([]);
  const flowNodesRef = useRef([]);
  const flowEdgesRef = useRef([]);
  const flowStatusRef = useRef('idle'); // 'idle', 'running', 'paused', 'completed'
  const flowCurrentNodeIdRef = useRef(null);
  const flowVariablesRef = useRef({});
  const isEscalatedRef = useRef(false);

  const easyflowConversationIdRef = useRef(null);

  const saveEasyFlowMessage = async (role, message) => {
    if (!easyflowConversationIdRef.current) return;
    try {
      const token = (() => {
        const user = sessionStorage.getItem('user');
        if (user) return JSON.parse(user).token;
        return null;
      })();
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';

      await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/easyflow/conversations/${easyflowConversationIdRef.current}/messages?ai_agent_id=${aiAgentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          role: role,
          message: message,
          metadata: {}
        })
      });
    } catch (err) {
      console.error('Error saving easyflow message:', err);
    }
  };

  const createEasyFlowConversation = async (flowId, aiAgentId) => {
    try {
      const token = (() => {
        const user = sessionStorage.getItem('user');
        if (user) return JSON.parse(user).token;
        return null;
      })();

      const rawSessionId = localStorage.getItem('chat_interface_session_id') || sessionId;
      const convoSessionId = rawSessionId || crypto.randomUUID();

      // Sync active session ID so WebSocket and other components use the correct UUID
      setSessionId(convoSessionId);
      localStorage.setItem('chat_interface_session_id', convoSessionId);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/easyflow/conversations?ai_agent_id=${aiAgentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          flow_id: flowId,
          session_id: convoSessionId,
          channel: 'widget',
          ai_agent_id: aiAgentId
        })
      });
      if (response.ok) {
        const resData = await response.json();
        easyflowConversationIdRef.current = resData.session_id;
        console.log('Created EasyFlow Conversation:', resData.session_id);
      }
    } catch (err) {
      console.error('Error creating EasyFlow conversation:', err);
    }
  };

  // Inactivity session timeout state and ref
  const [isExpired, setIsExpired] = useState(false);
  const sessionTimeoutRef = useRef(null);

  const startSessionTimeout = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    sessionTimeoutRef.current = setTimeout(() => {
      setIsExpired(true);
      setMessages(prev => [
        ...prev,
        {
          id: `expired_${Date.now()}`,
          text: 'Your chat session has expired after 30 minutes of inactivity.',
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })
        }
      ]);
      localStorage.removeItem('chat_interface_session_id');
    }, 30 * 60 * 1000); // 30 minutes
  };

  const resetSessionTimeout = () => {
    startSessionTimeout();
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getFirstEdge = (sourceId) => {
    const edge = flowEdgesRef.current.find(e => e.source === sourceId);
    return edge ? edge.target : null;
  };

  const getEdgeByHandle = (sourceId, handle) => {
    const edge = flowEdgesRef.current.find(e => e.source === sourceId && e.sourceHandle === handle);
    return edge ? edge.target : null;
  };

  const substituteVariables = (text) => {
    if (!text) return text;
    return text.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
      const key = path.trim();
      const val = flowVariablesRef.current[key];
      return val !== undefined && val !== null ? String(val) : match;
    });
  };

  const appendBotMessage = (text) => {
    setMessages(prev => [
      ...prev,
      {
        id: `bot_${Date.now()}_${Math.random()}`,
        text: text,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })
      }
    ]);
    saveEasyFlowMessage('BOT', text);
  };

  const appendSystemMessage = (text) => {
    setMessages(prev => [
      ...prev,
      {
        id: `system_${Date.now()}_${Math.random()}`,
        text: text,
        sender: 'system',
        timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })
      }
    ]);
  };

  const evaluateRule = (rule) => {
    if (!rule) return false;
    const { field, operator, value } = rule;

    let resolvedField = field;
    if (resolvedField.startsWith('webhook.') && !resolvedField.startsWith('webhookResponse.')) {
      resolvedField = 'webhookResponse.' + resolvedField.slice('webhook.'.length);
    }

    const varValue = resolvedField.includes('.')
      ? resolvedField.split('.').reduce((o, k) => o?.[k], flowVariablesRef.current)
      : flowVariablesRef.current[resolvedField];

    const strVal = String(varValue ?? '');
    const ruleVal = String(value ?? '');

    switch (operator) {
      case 'equals':            return strVal === ruleVal;
      case 'not_equals':        return strVal !== ruleVal;
      case 'contains':          return strVal.toLowerCase().includes(ruleVal.toLowerCase());
      case 'starts_with':       return strVal.toLowerCase().startsWith(ruleVal.toLowerCase());
      case 'ends_with':         return strVal.toLowerCase().endsWith(ruleVal.toLowerCase());
      case 'greater_than':      return Number(varValue) > Number(value);
      case 'less_than':         return Number(varValue) < Number(value);
      case 'is_empty':          return !varValue || strVal.trim() === '';
      case 'is_not_empty':      return !!varValue && strVal.trim() !== '';
      case 'image_exists':      return !!varValue && strVal !== 'null' && strVal.trim() !== '';
      case 'upload_missing':    return !varValue || strVal === 'null' || strVal.trim() === '';
      case 'invalid_file_type': return flowVariablesRef.current[field.replace(/\.url$/, '.error')] === 'invalid_file_type';
      case 'matches_regex': {
        try { return new RegExp(ruleVal).test(strVal); } catch { return false; }
      }
      default: return false;
    }
  };

  const resolveConditionRoute = (nodeId, data) => {
    const branches = data.branches || [];

    for (const branch of branches) {
      if (evaluateRule(branch.rule)) {
        if (branch.targetNodeId) return branch.targetNodeId;
        const edgeTarget = getEdgeByHandle(nodeId, branch.id);
        if (edgeTarget) return edgeTarget;
      }
    }

    if (data.defaultTargetNodeId) return data.defaultTargetNodeId;

    return getFirstEdge(nodeId);
  };

  const resolveInputRoute = (currentNodeId, text) => {
    const outgoing = flowEdgesRef.current.filter(e => e.source === currentNodeId);
    let fallback = null;

    for (const edge of outgoing) {
      const targetNode = flowNodesRef.current.find(n => n.id === edge.target);
      if (!targetNode) continue;

      if (!edge.sourceHandle && targetNode.data?.type !== 'input') {
        fallback = edge.target;
      }

      if (targetNode.data?.keywords) {
        const keywords = targetNode.data.keywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean);
        if (keywords.some(k => text.toLowerCase().includes(k))) {
          return edge.target;
        }
      }
    }
    return fallback;
  };

  const executeBotFlowNode = async (nodeId) => {
    flowCurrentNodeIdRef.current = nodeId;
    const node = flowNodesRef.current.find(n => n.id === nodeId);
    if (!node) {
      flowStatusRef.current = 'completed';
      appendSystemMessage('End of Flow');
      return;
    }

    const data = node.data || {};

    switch (data.type) {
      case 'start': {
        const next = getFirstEdge(nodeId);
        if (next) await executeBotFlowNode(next);
        else {
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
        }
        break;
      }

      case 'message': {
        if (data.messages && data.messages.length > 0) {
          for (const msg of data.messages) {
            if (msg.text) {
              await new Promise(r => setTimeout(r, 600));
              appendBotMessage(substituteVariables(msg.text));
            }
          }
        }

        const buttons = [];
        if (data.messages) {
          data.messages.forEach(m => {
            if (m.buttons) buttons.push(...m.buttons);
          });
        }

        const outgoing = flowEdgesRef.current.filter(e => e.source === nodeId);
        const hasKeywordChildren = outgoing.some(e => {
          const t = flowNodesRef.current.find(n => n.id === e.target);
          return t?.data?.type === 'input' && t.data?.keywords;
        });

        if (buttons.length > 0 || hasKeywordChildren) {
          flowStatusRef.current = 'paused';
          if (buttons.length > 0) {
            setActiveButtons(buttons);
          }
        } else {
          const next = getFirstEdge(nodeId);
          if (next) await executeBotFlowNode(next);
          else {
            flowStatusRef.current = 'completed';
            appendSystemMessage('End of Flow');
          }
        }
        break;
      }

      case 'input': {
        if (data.inputPrompt) {
          await new Promise(r => setTimeout(r, 600));
          appendBotMessage(substituteVariables(data.inputPrompt));
        }

        const next = getFirstEdge(nodeId);
        if (next) {
          flowStatusRef.current = 'paused';
        } else {
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
        }
        break;
      }

      case 'condition': {
        const nextId = resolveConditionRoute(nodeId, data);
        if (nextId) await executeBotFlowNode(nextId);
        else {
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
        }
        break;
      }

      case 'delay': {
        setIsLoading(true);
        setAgentStatus('Typing..');
        await new Promise(r => setTimeout(r, data.delayMs || 1000));
        setIsLoading(false);
        setAgentStatus(null);
        const next = getFirstEdge(nodeId);
        if (next) await executeBotFlowNode(next);
        else {
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
        }
        break;
      }

      case 'goto': {
        if (data.isFlowJump && data.targetFlowId) {
          try {
            const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
            const token = (() => {
              const user = sessionStorage.getItem('user');
              if (user) return JSON.parse(user).token;
              return null;
            })();
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bot-flows/${data.targetFlowId}?ai_agent_id=${aiAgentId}`, {
              headers: {
                ...(token && { 'Authorization': `Bearer ${token}` }),
              }
            });
            if (response.ok) {
              const resJson = await response.json();
              if (resJson && resJson.content) {
                let flowData = resJson;
                if (typeof resJson.content === 'string') {
                  flowData = JSON.parse(resJson.content);
                }
                if (flowData.nodes) {
                  flowNodesRef.current = flowData.nodes;
                  flowEdgesRef.current = flowData.edges || [];
                  const startNode = flowNodesRef.current.find(n => n.data?.type === 'start');
                  if (startNode) {
                    await executeBotFlowNode(startNode.id);
                    break;
                  }
                }
              }
            }
          } catch (e) {
            console.error('Failed to load target flow in goto node:', e);
          }
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
          break;
        }

        if (data.targetNodeId) await executeBotFlowNode(data.targetNodeId);
        else {
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
        }
        break;
      }

      case 'attribute': {
        const attrName = data.attributeName === 'custom' ? data.variableName : data.attributeName;
        if (attrName && data.attributeValue !== undefined) {
          flowVariablesRef.current[attrName] = data.attributeValue;
        }
        const next = getFirstEdge(nodeId);
        if (next) await executeBotFlowNode(next);
        else {
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
        }
        break;
      }

      case 'ai_node': {
        setIsLoading(true);
        setAgentStatus('Typing..');
        const resolvedInput = substituteVariables(data.aiConfig?.userInput || '{{lastUserInput}}');
        try {
          const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
          const token = (() => {
            const user = sessionStorage.getItem('user');
            if (user) return JSON.parse(user).token;
            return null;
          })();

          if (!flowVariablesRef.current['aiConversationHistory']) {
            flowVariablesRef.current['aiConversationHistory'] = [];
          }

          const payload = {
            userInput: resolvedInput,
            systemPromptOverride: substituteVariables(data.aiConfig?.systemPromptOverride) || undefined,
            sessionId: sessionId,
            conversationHistory: flowVariablesRef.current['aiConversationHistory'],
            temperature: data.aiConfig?.temperature,
            maxTokens: data.aiConfig?.maxTokens,
            topP: data.aiConfig?.topP
          };

          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/ai-node/execute?ai_agent_id=${aiAgentId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: JSON.stringify(payload),
          });
          setIsLoading(false);
          setAgentStatus(null);
          if (response.ok) {
            const resJson = await response.json();
            if (resJson.success && resJson.data) {
              const botReply = resJson.data.output || '';
              if (Array.isArray(resJson.data.conversationHistory)) {
                flowVariablesRef.current['aiConversationHistory'] = resJson.data.conversationHistory;
              }
              const outputVar = data.aiConfig?.outputVariable || 'aiResponse';
              flowVariablesRef.current[outputVar] = botReply;
              appendBotMessage(botReply);

              if (resJson.data.session_id && (!sessionId || sessionId === 'None' || sessionId === 'null')) {
                setSessionId(resJson.data.session_id);
                localStorage.setItem('chat_interface_session_id', resJson.data.session_id);
                startSessionTimeout();
                fetchSessions();
              }
            }
          }
        } catch (e) {
          setIsLoading(false);
          setAgentStatus(null);
        }

        const next = getFirstEdge(nodeId);
        if (next) await executeBotFlowNode(next);
        else {
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
        }
        break;
      }

       case 'function': {
        const DEFAULT_FUNC = { code: '', inputMapping: {}, outputMapping: {} };
        const funcConfig = { ...DEFAULT_FUNC, ...(data.functionConfig || {}) };
        try {
          const inputContext = { ...flowVariablesRef.current };
          // eslint-disable-next-line no-new-func
          const func = Function(`
            try {
              const input = arguments[0];
              ${funcConfig.code}
            } catch (e) {
              return { __error: e.message };
            }
          `);
          const result = func(inputContext);
          if (result && result.__error) {
            console.error('Function Node Execution Error:', result.__error);
          } else if (result && typeof result === 'object') {
            Object.entries(result).forEach(([key, value]) => {
              flowVariablesRef.current[key] = value;
            });
          }
        } catch (err) {
          console.error('Function Node Syntax/Compile Error:', err);
        }
        const next = getFirstEdge(nodeId);
        if (next) await executeBotFlowNode(next);
        else {
          flowStatusRef.current = 'completed';
          appendSystemMessage('End of Flow');
        }
        break;
      }

      case 'escalate': {
        appendSystemMessage('Escalating to human assistant...');
        const reason = data.reason || 'User requested human assistant via bot flow';
        try {
          const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
          const token = (() => {
            const user = sessionStorage.getItem('user');
            if (user) return JSON.parse(user).token;
            return null;
          })();
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/handoff/request?ai_agent_id=${aiAgentId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: JSON.stringify({
              session_id: easyflowConversationIdRef.current || sessionId || crypto.randomUUID(),
              reason: reason
            })
          });
          await new Promise(r => setTimeout(r, 400));
          if (response.ok) {
            const result = await response.json();
            if (result && result.status === 'active' && result.assigned_to_name) {
              appendBotMessage(`I have connected you to human agent ${result.assigned_to_name}. They will assist you now.`);
            } else {
              appendBotMessage(result?.message || 'A human agent has been requested. Someone will be with you shortly.');
            }
          } else {
            appendBotMessage('Failed to escalate. No human agent is available right now.');
          }
        } catch (e) {
          console.error('Error in escalate node:', e);
          appendBotMessage('Failed to escalate to a human agent due to a network error.');
        }
        isEscalatedRef.current = true;
        flowStatusRef.current = 'paused';
        break;
      }

      case 'integration': {
        const intConfig = data.integrationConfig || {};
        if (intConfig.integrationType === 'freshchat' && intConfig.action === 'handoff') {
          appendSystemMessage('Connecting you to a Freshchat human agent...');
          try {
            const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
            const token = (() => {
              const user = sessionStorage.getItem('user');
              if (user) return JSON.parse(user).token;
              return null;
            })();
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/handoff/request?ai_agent_id=${aiAgentId}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
              },
              body: JSON.stringify({
                session_id: easyflowConversationIdRef.current || sessionId || crypto.randomUUID(),
                reason: 'Freshchat handoff via integration node',
                integration_target: 'freshchat'
              })
            });
            await new Promise(r => setTimeout(r, 400));
            if (response.ok) {
              appendBotMessage('A human agent has been requested on Freshchat. They will be with you shortly.');
            } else {
              appendBotMessage('Freshchat handoff could not be completed at this time.');
            }
          } catch (e) {
            console.error('Error in freshchat handoff node:', e);
            appendBotMessage('Freshchat connection failed due to network error.');
          }
          isEscalatedRef.current = true;
          flowStatusRef.current = 'paused';
        } else {
          const next = getFirstEdge(nodeId);
          if (next) await executeBotFlowNode(next);
          else {
            flowStatusRef.current = 'completed';
          }
        }
        break;
      }

      default: {
        const next = getFirstEdge(nodeId);
        if (next) await executeBotFlowNode(next);
        else {
          flowStatusRef.current = 'completed';
        }
        break;
      }
    }
  };

  const handleBotFlowUserInput = async (text) => {
    if (flowStatusRef.current !== 'paused') return;

    const currentNode = flowNodesRef.current.find(n => n.id === flowCurrentNodeIdRef.current);
    if (!currentNode) return;

    flowVariablesRef.current['lastUserInput'] = text;

    if (currentNode.data?.type === 'message') {
      const nextId = resolveInputRoute(currentNode.id, text);
      if (nextId) {
        flowStatusRef.current = 'running';
        setActiveButtons([]);
        await executeBotFlowNode(nextId);
      }
      return;
    }

    if (currentNode.data?.type === 'input') {
      if (currentNode.data?.variableName) {
        flowVariablesRef.current[currentNode.data.variableName] = text;
      }
      flowStatusRef.current = 'running';
      setActiveButtons([]);

      const nextId = resolveInputRoute(currentNode.id, text) || getFirstEdge(currentNode.id);
      if (nextId) {
        await executeBotFlowNode(nextId);
      } else {
        flowStatusRef.current = 'completed';
      }
    }
  };

  const loadActiveBotFlow = async (aiAgentId, autoTrigger = false) => {
    try {
      const token = (() => {
        const user = sessionStorage.getItem('user');
        if (user) return JSON.parse(user).token;
        return null;
      })();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/active-bot-flow?ai_agent_id=${aiAgentId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'X-Widget-Preview': 'true'
        }
      });
      if (response.ok) {
        const data = await response.json();
        flowNodesRef.current = data.nodes || [];
        flowEdgesRef.current = data.edges || [];
        console.log('Loaded botflow inside ChatInterface:', flowNodesRef.current.length, 'nodes');

        if (autoTrigger && flowStatusRef.current === 'idle' && flowNodesRef.current.length > 0) {
          const startNode = flowNodesRef.current.find(n => n.data?.type === 'start');
          if (startNode) {
            flowStatusRef.current = 'running';
            flowVariablesRef.current = {};
            if (data.flow_id) {
              await createEasyFlowConversation(data.flow_id, aiAgentId);
            }
            executeBotFlowNode(startNode.id);
          }
        }
      }
    } catch (err) {
      console.error('Error loading botflow config:', err);
    }
  };

  const fetchWidgetConfig = async () => {
    try {
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      const token = (() => {
        const user = sessionStorage.getItem('user');
        if (user) return JSON.parse(user).token;
        return null;
      })();
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/config`, {
        headers: {
          'Content-Type': 'application/json',
          'X-AI-Agent-ID': aiAgentId,
          'X-Widget-Preview': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const config = await response.json();
        setStreamingEnabled(config.streaming_enabled !== undefined ? config.streaming_enabled : true);
        if (config.response_mode === 'botflow') {
          setResponseMode('botflow');
          setHistoryLoaded(true);
          loadActiveBotFlow(aiAgentId);
        } else {
          setResponseMode('ai');
          loadChatHistory();
        }
      } else {
        loadChatHistory();
      }
    } catch (error) {
      console.error('Error fetching widget config:', error);
      loadChatHistory();
    }
  };

  useEffect(() => {
    fetchLogo();
    fetchWidgetConfig();
    fetchSessions(1);

    const storedSessionId = localStorage.getItem('chat_interface_session_id');
    if (storedSessionId && storedSessionId !== 'None' && storedSessionId !== 'null' && storedSessionId !== 'undefined') {
      startSessionTimeout();
    }

    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- WebSocket Real-Time Integration for Agent Messages ---
  useEffect(() => {
    if (!sessionId || sessionId === 'None' || sessionId === 'null') return;

    let socket = null;
    let reconnectTimeout = null;
    let shouldReconnect = true;

    const connectWebSocket = () => {
      if (!sessionId || sessionId === 'None' || sessionId === 'null') return;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.REACT_APP_API_BASE_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      const wsUrl = `${protocol}//${wsHost}/api/ws/widget?session_id=${sessionId}&ai_agent_id=${aiAgentId}`;

      console.log('🔌 Connecting Widget WebSocket:', wsUrl);
      socket = new WebSocket(wsUrl);

      // Store socket in a ref so we can kill it on New Chat
      window._activeChatSocket = socket;

      socket.onopen = () => {
        console.log('✅ Widget WebSocket Connected');
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📥 Widget WS Message Received:', data);

          if (data.type === 'NEW_MESSAGE') {
            const role = data.message.role;
            if (role === 'assistant' || role === 'human_agent' || role === 'agent' || role === 'user' || role === 'human') {
              setMessages(prev => {
                // Avoid duplicates by checking content and role
                const exists = prev.some(m =>
                  m.text === data.message.content &&
                  (m.sender === 'bot' || m.sender === 'human_agent' || m.sender === 'assistant' || m.sender === 'user' || m.sender === 'human')
                );
                if (exists) return prev;

                return [...prev, {
                  id: Date.now() + Math.random(),
                  text: data.message.content,
                  sender: (role === 'user' || role === 'human') ? 'user' : (role === 'human_agent' ? 'human_agent' : 'bot'),
                  timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })
                }];
              });
            }
          } else if (data.type === 'SESSION_STATUS') {
            console.log('🔄 Session Status Update:', data.status);
            setSessionStatus(data.status);

            if (data.status === 'resolved') {
              showToast('Session resolved by agent.', 'info');
              // ELITE: Don't clear messages immediately, let the user see the final history.
              // We just refresh the sidebar to show it's updated.
              fetchSessions();
            }
          }
        } catch (err) {
          console.error('❌ Widget WS Parsing Error:', err);
        }
      };

      socket.onclose = () => {
        if (!shouldReconnect) return;
        console.log('🔌 Widget WebSocket Disconnected. Reconnecting...');
        reconnectTimeout = setTimeout(connectWebSocket, 3000);
      };

      socket.onerror = (err) => {
        console.error('❌ Widget WebSocket Error:', err);
        socket.close();
      };
    };

    connectWebSocket();

    return () => {
      shouldReconnect = false;
      if (socket) socket.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchLogo = async () => {
    try {
      const data = await ApiClient.getCurrentLogo();
      if (data && data.exists && !data.isDefault && data.logo_url) {
        setLogoUrl(data.logo_url);
      } else {
        setLogoUrl('/default.png');
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
      setLogoUrl('/default.png');
    }
  };

  const loadChatHistory = async () => {
    const storedSessionId = localStorage.getItem('chat_interface_session_id');
    // Don't call history endpoint until we have a real Agno-generated session ID
    if (!storedSessionId || storedSessionId === 'None' || storedSessionId === 'null' || storedSessionId === 'undefined') {
      // Clear any invalid stored session ID
      localStorage.removeItem('chat_interface_session_id');
      setMessages([]);
      setHistoryLoaded(true);
      return;
    }


    try {
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      const token = (() => {
        const user = sessionStorage.getItem('user');
        if (user) return JSON.parse(user).token;
        return null;
      })();
      
      // Fetch session handoff status
      try {
        const statusRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/chat/status/${storedSessionId}`, {
          headers: {
            'X-AI-Agent-ID': aiAgentId,
            ...(token && { 'Authorization': `Bearer ${token}` }),
          }
        });
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setSessionStatus(statusData.status || 'open');
        }
      } catch (statusErr) {
        console.error('Error fetching chat status:', statusErr);
      }

      const history = await ApiClient.getWidgetHistory(storedSessionId, aiAgentId);
      if (history && history.length > 0) {
        // Filter out messages with null/empty content (Agno intermediate messages)
        const validMessages = history.filter(
          msg => msg.message && String(msg.message).trim() !== ''
        );
        if (validMessages.length > 0) {
          const restored = validMessages.map((msg, idx) => ({
            id: `history_${idx}`,
            text: cleanErrorMessage(String(msg.message)),
            sender: msg.type,
            image: msg.image || null,
            timestamp: new Date(msg.timestamp).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }),
          }));
          setMessages(restored);
          setHistoryLoaded(true);
          return;
        }
      }
    } catch (err) {
      console.error('Error loading chat history:', err);
    }

    // No history found — keep empty
    setMessages([]);
    setHistoryLoaded(true);
  };

  const fetchSessions = async (page = currentPage) => {
    try {
      setIsSessionsLoading(true);
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      const data = await ApiClient.getWidgetSessions(aiAgentId, page, SESSIONS_PER_PAGE);

      setSessions(data.sessions || []);
      setTotalSessions(data.total_count || 0);
      setCurrentPage(data.page || page);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setIsSessionsLoading(false);
    }
  };

  const handleSelectSession = (session) => {
    const id = session.session_id;
    if (id === sessionId) return;
    easyflowConversationIdRef.current = null;
    setSessionId(id);
    localStorage.setItem('chat_interface_session_id', id);
    setSessionStatus(session.handoff_status || 'open');
    setIsExpired(false);
    setMessages([]); // Clear current view
    setHistoryLoaded(false);
    // loadChatHistory will be triggered if we use a useEffect or just call it directly
    setTimeout(() => loadChatHistory(), 100);
    startSessionTimeout();
  };

  const handleNewChat = () => {
    // ELITE: Generate a truly unique ID on the frontend to prevent backend hijacking
    const newId = crypto.randomUUID();

    // ELITE: Kill active socket to prevent session bleeding
    if (window._activeChatSocket) {
      console.log('🔌 Force closing old session socket...');
      window._activeChatSocket.close();
      window._activeChatSocket = null;
    }

    // ELITE: Triple-lock reset with NEW ID
    setSessionId(newId);
    localStorage.setItem('chat_interface_session_id', newId);
    setHistoryLoaded(true);
    setIsExpired(false);
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    easyflowConversationIdRef.current = null;
    setAgentStatus(null);
    setSessionStatus('open');

    if (responseMode === 'botflow') {
      flowStatusRef.current = 'idle';
      setMessages([]);
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      loadActiveBotFlow(aiAgentId, true);
    } else {
      setMessages([]);
    }
    console.log('✨ Fresh Session Started:', newId);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('Please select a valid image file.', 'warning');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size must be less than 5MB.', 'warning');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target.result);
        setSelectedFileName(file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaste = (e) => {
    if (isLoading || isExpired || sessionStatus === 'resolved') return;
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          if (file.size > 5 * 1024 * 1024) {
            showToast('Image size must be less than 5MB.', 'warning');
            return;
          }
          const reader = new FileReader();
          reader.onload = (event) => {
            setSelectedImage(event.target.result);
            setSelectedFileName(file.name || `pasted-image-${Date.now()}.png`);
            showToast(`Image pasted: ${file.name || 'pasted-image.png'}`);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const removeAttachment = () => {
    setSelectedImage(null);
    setSelectedFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteSession = (id) => {
    setSessionToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    setIsDeletingSession(true);
    try {
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      await ApiClient.deleteWidgetSession(sessionToDelete, aiAgentId);

      setSessions(prev => prev.filter(s => s.session_id !== sessionToDelete));
      if (sessionToDelete === sessionId) {
        handleNewChat();
      }
      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error('Error deleting session:', err);
    } finally {
      setIsDeletingSession(false);
      setSessionToDelete(null);
    }
  };


  const sendMessage = async () => {
    if ((!inputMessage.trim() && !selectedImage) || isLoading || isExpired || sessionStatus === 'resolved') return;

    resetSessionTimeout();

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      image: selectedImage, // Keep the image for local display
      timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })
    };

    const currentInput = inputMessage;
    setInputMessage('');

    // Smooth physics glide down when transitioning from centered state
    if (messages.length === 0) {
      setIsFirstSubmitting(true);
      await new Promise(r => setTimeout(r, 280));
    }

    setMessages(prev => [...prev, userMessage]);
    setIsFirstSubmitting(false);

    if (responseMode === 'botflow' && !isEscalatedRef.current) {
      saveEasyFlowMessage('USER', currentInput);
      await handleBotFlowUserInput(currentInput);
      return;
    }

    setIsLoading(true);
    setAgentStatus('Typing..'); // Immediate feedback

    try {
      const token = (() => {
        const user = sessionStorage.getItem('user');
        if (user) return JSON.parse(user).token;
        return null;
      })();

      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      // ELITE: Use the Widget-specific chat endpoint to ensure session persistence in agent tables
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AI-Agent-ID': aiAgentId,
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          message: currentInput,
          session_id: sessionId,
          stream: streamingEnabled,
          image: selectedImage,
          learning_enabled: learningEnabled,
          metadata: coords ? {
            latitude: coords.latitude,
            longitude: coords.longitude
          } : {}
        }),
      });

      // Clear the selected image after sending
      setSelectedImage(null);
      setSelectedFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';

      if (response.status === 401) {
        sessionStorage.removeItem('user');
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        let errMsg = `HTTP error! status: ${response.status}`;
        try {
          const errData = JSON.parse(await response.text());
          if (errData && errData.detail) {
            errMsg = errData.detail;
          }
        } catch (e) {}
        throw new Error(errMsg);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        const botReply = data.response;
        const newSessionId = data.session_id;

        if (newSessionId && (!sessionId || sessionId === 'None' || sessionId === 'null')) {
          console.log('🔗 Anchoring to session:', newSessionId);
          setSessionId(newSessionId);
          localStorage.setItem('chat_interface_session_id', newSessionId);
          startSessionTimeout();
          fetchSessions();
        }

        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          text: botReply || '',
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }),
        }]);
        setIsLoading(false);
        setAgentStatus(null);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let botMessageTracker = { id: null, text: '' };

      let buffer = '';
      let hasReceivedChunk = false;

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
          buffer += decoder.decode(value, { stream: true });

          let lines = buffer.split('\n');
          buffer = lines.pop(); // Keep incomplete chunk in buffer 

          for (let line of lines) {
            if (line.trim() === '') continue;

            if (line.startsWith('data: ')) {
              try {
                const rawData = line.substring(6);
                if (rawData.trim() === '[DONE]') {
                  setIsLoading(false);
                  setAgentStatus(null);
                  continue;
                }
                const payload = JSON.parse(rawData);
                hasReceivedChunk = true;

                if (payload.type === 'metadata') {
                  const newSessionId = payload.session_id;
                  // ELITE: Only update if we don't have a valid session ID yet (Prevent session splitting)
                  if (newSessionId && (!sessionId || sessionId === 'None' || sessionId === 'null')) {
                    console.log('🔗 Anchoring to session:', newSessionId);
                    setSessionId(newSessionId);
                    localStorage.setItem('chat_interface_session_id', newSessionId);
                    startSessionTimeout();
                    fetchSessions();
                  }
                } else if (payload.type === 'handoff_requested') {
                  // ELITE: Handle handoff signal to lock UI
                  setIsLoading(false);
                  setAgentStatus('Human agent connecting...');
                } else if (payload.type === 'human_controlled') {
                  setIsLoading(false);
                  setAgentStatus(null);
                } else if (payload.type === 'status') {
                  if (payload.append) {
                    setAgentStatus(prev => (prev || '') + payload.data);
                  } else {
                    setAgentStatus(payload.data);
                  }
                } else if (payload.type === 'content') {
                  if (!botMessageTracker.id) {
                    botMessageTracker.id = Date.now() + 1;
                    botMessageTracker.text = cleanErrorMessage(payload.data || '');
                    setMessages(prev => [...prev, {
                      id: botMessageTracker.id,
                      text: botMessageTracker.text,
                      sender: payload.role === 'human_agent' ? 'human_agent' : 'bot',
                      timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }),
                    }]);
                    setAgentStatus(null);
                  } else {
                    botMessageTracker.text += payload.data || '';
                    const currentId = botMessageTracker.id;
                    const currentText = cleanErrorMessage(botMessageTracker.text);
                    setMessages(prev => prev.map(msg =>
                      msg.id === currentId ? { ...msg, text: currentText } : msg
                    ));
                  }
                } else if (payload.type === 'error') {
                  console.error('❌ Guardrail Error Received:', payload.data);
                  setMessages(prev => [...prev, {
                    id: Date.now(),
                    text: cleanErrorMessage(payload.data),
                    sender: 'bot',
                    timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }),
                    isError: true
                  }]);
                  setIsLoading(false);
                  setAgentStatus(null);
                }
              } catch (e) {
                // Ignore raw or malformed lines
              }
            }
          }
        }
      }
      if (!hasReceivedChunk) {
        throw new Error("Connection error or invalid configuration. Please try again.");
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: error.message || 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
      fetchSessions(); // Refresh sidebar after every message to keep titles/order updated
    }
  };


  const handleFeedbackSubmit = async () => {
    try {
      await ApiClient.submitFeedback(sessionId, rating, comment);
      console.log('Feedback submitted successfully');
      setShowFeedbackModal(false);
      // Optional: you can trigger onLogout() here if you want ending chat to also log out, 
      // or simply clear messages to start fresh.
      setMessages([]);
      setSessionId(null); // Clear session - Agno will generate a new one on next message
      localStorage.removeItem('chat_interface_session_id');
      setRating(0);
      setComment('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Mobile backdrop overlay when sidebar is open on small screens */}
        {!isSidebarCollapsed && (
          <div
            className="copilot-sidebar-backdrop"
            onClick={() => setIsSidebarCollapsed(true)}
          />
        )}

        <ChatSidebar
          logoUrl={logoUrl}
          onAdminPanel={onAdminPanel}
          sessions={sessions}
          activeSessionId={sessionId}
          onSelectSession={(s) => {
            handleSelectSession(s);
            if (window.innerWidth <= 768) {
              setIsSidebarCollapsed(true);
            }
          }}
          onNewChat={() => {
            handleNewChat();
            if (window.innerWidth <= 768) {
              setIsSidebarCollapsed(true);
            }
          }}
          onDeleteSession={handleDeleteSession}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          currentPage={currentPage}
          totalSessions={totalSessions}
          onPageChange={fetchSessions}
          pageSize={SESSIONS_PER_PAGE}
          isLoading={isSessionsLoading}
          user={user}
          onLogout={onLogout}
        />

        <div style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          background: '#ffffff',
          position: 'relative'
        }}>
          {/* Ambient Background Gradient & Interactive Fluid Water-Ripple Grid Canvas (New Chat only) */}
          {messages.length === 0 && historyLoaded && (
            <>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                left: 0,
                height: '420px',
                background: 'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(216, 235, 255, 0.45) 0%, rgba(254, 230, 240, 0.35) 45%, rgba(255, 255, 255, 0) 80%)',
                pointerEvents: 'none',
                zIndex: 0
              }} />

              <WaterGridBackground
                gridSize={28}
                lineColor="rgba(24, 24, 27, 0.08)"
                maskCenter="50% 45%"
              />
            </>
          )}

          {/* Top Cortex-style Navigation Bar */}
          <div
            className="copilot-top-nav"
            style={{
              height: '64px',
              padding: '0 28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 10,
              borderBottom: '1px solid rgba(241, 242, 244, 0.6)'
            }}
          >
            {/* Left Header: Hamburger on mobile when sidebar is closed */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                className="copilot-hamburger-btn"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e4e4e7',
                  borderRadius: '10px',
                  color: '#18181b',
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  transition: 'background 0.15s ease'
                }}
                title={isSidebarCollapsed ? "Open sidebar" : "Close sidebar"}
              >
                <Menu size={18} />
              </button>
            </div>

            {/* Right: Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {(["admin", "super_admin"].includes(user?.role || JSON.parse(sessionStorage.getItem("user") || "{}").role)) && (
                <button
                  onClick={onAdminPanel}
                  style={{
                    padding: '7px 14px',
                    background: '#ffffff',
                    border: '1px solid #e4e4e7',
                    borderRadius: '18px',
                    color: '#52525b',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f4f4f5')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                >
                  <Settings size={14} />
                  <span>Admin</span>
                </button>
              )}

              <button
                onClick={() => {
                  const exportText = messages.map(m => `${m.sender.toUpperCase()}: ${m.text}`).join('\n\n');
                  const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `easychat-conversation-${sessionId || 'session'}.txt`;
                  link.click();
                  URL.revokeObjectURL(url);
                }}
                style={{
                  padding: '7px 14px',
                  background: '#ffffff',
                  border: '1px solid #e4e4e7',
                  borderRadius: '18px',
                  color: '#52525b',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f4f4f5')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                title="Export chat"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" x2="12" y1="15" y2="3" />
                </svg>
                <span className="copilot-hide-mobile">Export chat</span>
              </button>

              <button
                onClick={() => (window.location.href = '/billing')}
                style={{
                  padding: '7px 14px',
                  background: '#18181b',
                  border: 'none',
                  borderRadius: '18px',
                  color: '#ffffff',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#27272a')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#18181b')}
              >
                Upgrade
              </button>
            </div>
          </div>

          {/* Central Workspace / Messages Area */}
          <div
            className="copilot-messages-container custom-scrollbar"
            onDragStart={(e) => e.preventDefault()}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px 28px 10px 28px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}
          >

            {/* Empty State / Centered Welcome Screen */}
            {messages.length === 0 && historyLoaded && (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '820px',
                margin: '0 auto',
                width: '100%',
                padding: '40px 28px 20px 28px',
                animation: 'fadeIn 0.3s ease',
                pointerEvents: isFirstSubmitting ? 'none' : 'auto'
              }}>
                {/* Header */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: '28px',
                  opacity: isFirstSubmitting ? 0 : 1,
                  transform: isFirstSubmitting ? 'translateY(-20px)' : 'translateY(0)',
                  transition: 'opacity 0.24s ease, transform 0.24s ease'
                }}>
                  <h1 style={{
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: '#18181b',
                    letterSpacing: '-0.025em',
                    margin: '0 0 8px 0',
                    fontFamily: 'inherit'
                  }}>
                    Hello, {displayName} 👋
                  </h1>
                  <p style={{
                    fontSize: '1.05rem',
                    color: '#6b7280',
                    margin: 0,
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    fontFamily: 'inherit'
                  }}>
                    How can I help you?
                  </p>
                </div>

                {/* Target anchor where the unified input dock aligns */}
                <div ref={centerAnchorRef} style={{ width: '100%', height: '1px', visibility: 'hidden' }} />
              </div>
            )}

            {!historyLoaded ? (
              <div className="w-full max-w-[820px] mx-auto px-4 py-8 space-y-6 animate-pulse">
                {/* Assistant bubble skeleton */}
                <div className="flex items-start gap-3 max-w-[80%]">
                  <div className="w-8 h-8 rounded-full bg-zinc-200 shrink-0" />
                  <div className="space-y-2 flex-1">
                    <div className="h-4 w-40 bg-zinc-200 rounded-lg" />
                    <div className="h-16 w-full bg-zinc-100 rounded-2xl rounded-tl-sm p-4 border border-zinc-200/60" />
                  </div>
                </div>

                {/* User message bubble skeleton */}
                <div className="flex items-start gap-3 max-w-[70%] ml-auto flex-row-reverse">
                  <div className="w-8 h-8 rounded-full bg-zinc-200 shrink-0" />
                  <div className="space-y-2 flex-1">
                    <div className="h-10 w-full bg-zinc-100 rounded-2xl rounded-tr-sm border border-zinc-200/60" />
                  </div>
                </div>

                {/* Assistant response skeleton */}
                <div className="flex items-start gap-3 max-w-[85%]">
                  <div className="w-8 h-8 rounded-full bg-zinc-200 shrink-0" />
                  <div className="space-y-2 flex-1">
                    <div className="h-20 w-full bg-zinc-100 rounded-2xl rounded-tl-sm p-4 border border-zinc-200/60" />
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                maxWidth: '820px',
                width: '100%',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {(() => {
                  let renderedSeparator = false;
                  return (
                    <>
                      {messages.map((message) => {
                        const isFirstHumanMessage = !renderedSeparator && message.sender === 'human_agent';
                        if (isFirstHumanMessage) {
                          renderedSeparator = true;
                        }
                        return (
                          <React.Fragment key={message.id}>
                            {isFirstHumanMessage && (
                              <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0', width: '100%' }}>
                                <div style={{ flex: 1, borderTop: '1px solid #bfdbfe' }}></div>
                                <span style={{ margin: '0 16px', padding: '6px 16px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.05em', color: '#1d4ed8', background: '#eff6ff', borderRadius: '999px', border: '1px solid #bfdbfe', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textTransform: 'uppercase' }}>
                                  Connected to Human Agent
                                </span>
                                <div style={{ flex: 1, borderTop: '1px solid #bfdbfe' }}></div>
                              </div>
                            )}
                            <div
                              className="animate-fadeIn message-group"
                              style={{
                                display: 'flex',
                                justifyContent: message.sender === 'user' ? 'flex-end' : (message.sender === 'system' ? 'center' : 'flex-start'),
                                width: '100%',
                                marginBottom: '20px'
                              }}
                            >
                              {message.sender === 'system' ? (
                                <div style={{
                                  fontSize: '10px',
                                  fontWeight: '800',
                                  color: '#cbd5e1',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.15em',
                                  margin: '12px 0'
                                }}>
                                  {message.text}
                                </div>
                              ) : (
                                <div
                                  className="copilot-message-bubble"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '12px',
                                    maxWidth: message.sender === 'user' ? '70%' : '100%',
                                    flexDirection: message.sender === 'user' ? 'row-reverse' : 'row'
                                  }}
                                >
                                  {(message.sender === 'bot' || message.sender === 'human_agent' || message.sender === 'assistant') && (
                                    <div
                                      role="presentation"
                                      aria-hidden="true"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onDragStart={(e) => { e.preventDefault(); return false; }}
                                      style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '12px',
                                        background: '#ffffff url(/default.png) center center / contain no-repeat',
                                        flexShrink: 0,
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                                        border: '1px solid #f1f2f4',
                                        marginTop: '2px',
                                        pointerEvents: 'none',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none'
                                      }}
                                    />
                                  )}

                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start',
                                    gap: '6px',
                                    flex: 1,
                                    minWidth: 0
                                  }}>
                                    <div
                                      className={message.sender === 'user' ? 'smart-card' : (message.sender === 'bot' && isLoading && message.id === messages[messages.length - 1]?.id ? 'animate-neuralSpark' : '')}
                                      style={{
                                        background: message.sender === 'user' ? '#18181b' : 'transparent',
                                        color: message.sender === 'user' ? '#ffffff' : '#18181b',
                                        padding: message.sender === 'user' ? '14px 20px' : '4px 0',
                                        borderRadius: message.sender === 'user' ? '20px 20px 4px 20px' : '0',
                                        border: 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        wordBreak: 'break-word',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        boxShadow: message.sender === 'user' ? '0 4px 14px rgba(0,0,0,0.1)' : 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        minHeight: message.sender === 'user' ? '42px' : 'auto',
                                        maxWidth: '100%'
                                      }}
                                    >
                                      {message.image && (
                                        <div style={{ marginBottom: '12px' }}>
                                          <img
                                            src={message.image.startsWith('http') || message.image.startsWith('data:')
                                              ? message.image
                                              : `${process.env.REACT_APP_API_BASE_URL}/widget/assets/image/${message.image}`}
                                            alt="Attached"
                                            draggable={false}
                                            onDragStart={(e) => e.preventDefault()}
                                            onError={(e) => {
                                              if (e.currentTarget?.parentElement) {
                                                e.currentTarget.parentElement.style.display = 'none';
                                              }
                                            }}
                                            style={{
                                              maxWidth: '100%',
                                              maxHeight: '300px',
                                              borderRadius: '12px',
                                              display: 'block',
                                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                              userSelect: 'none',
                                              WebkitUserDrag: 'none'
                                            }}
                                          />
                                        </div>
                                      )}
                                      <div
                                        key={message.text?.length}
                                        className={message.sender === 'bot' && isLoading && message.id === messages[messages.length - 1]?.id ? 'animate-textStream' : ''}
                                        style={{
                                          whiteSpace: 'pre-wrap',
                                          overflow: 'hidden',
                                          wordBreak: 'break-word',
                                          wordWrap: 'break-word',
                                          overflowWrap: 'break-word',
                                          textAlign: 'left',
                                          fontSize: '14.5px',
                                          lineHeight: '1.55'
                                        }}
                                      >
                                        {(() => {
                                          let text = typeof message.text === 'string'
                                            ? message.text.replace(/\s*<additional context>[\s\S]*?(?:<\/additional context>|$)/g, "").trim()
                                            : message.text;

                                          if (!text) return null;
                                          text = text.replace(/::IMAGE::(\S+)/g, '![image]($1)');
                                          const parts = text.split(/(<div class="chat-file".*?<\/div>|<img.*?>)/g);

                                          return parts.map((part, index) => {
                                            if (message.sender !== 'user' && (part.startsWith('<div class="chat-file"') || part.startsWith('<img'))) {
                                              return <div key={index} dangerouslySetInnerHTML={{ __html: part }} />;
                                            }
                                            return (
                                              <div key={index} style={{ whiteSpace: 'normal' }}>
                                                <ReactMarkdown
                                                  components={{
                                                    ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>,
                                                    ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>,
                                                    li: ({ children }) => <li style={{ margin: '4px 0' }}>{children}</li>,
                                                    p: ({ children }) => <p style={{ margin: '0 0 8px 0' }}>{children}</p>,
                                                    img: ({ src, alt }) => (
                                                      <img
                                                        src={src.startsWith('http') || src.startsWith('data:') ? src : `${process.env.REACT_APP_API_BASE_URL || ''}${src}`}
                                                        alt={alt}
                                                        draggable={false}
                                                        onDragStart={(e) => e.preventDefault()}
                                                        style={{
                                                          maxWidth: '100%',
                                                          maxHeight: '300px',
                                                          borderRadius: '12px',
                                                          marginTop: '8px',
                                                          display: 'block',
                                                          userSelect: 'none',
                                                          WebkitUserDrag: 'none'
                                                        }}
                                                      />
                                                    )
                                                  }}
                                                >
                                                  {part}
                                                </ReactMarkdown>
                                              </div>
                                            );
                                          });
                                        })()}
                                      </div>
                                    </div>

                                    <div className="message-timestamp" style={{
                                      fontSize: '10px',
                                      opacity: 0,
                                      color: '#a1a1aa',
                                      fontWeight: '600',
                                      textTransform: 'uppercase',
                                      padding: message.sender === 'user' ? '0 4px 0 0' : '0 0 0 4px',
                                      transition: 'opacity 0.2s ease'
                                    }}>
                                      {message.timestamp}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                })()}

                {sessionStatus === 'requested' && (
                  <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0', width: '100%' }}>
                    <div style={{ flex: 1, borderTop: '1px solid #fde68a' }}></div>
                    <span style={{ margin: '0 16px', padding: '6px 16px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.05em', color: '#b45309', background: '#fffbeb', borderRadius: '999px', border: '1px solid #fde68a', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textTransform: 'uppercase' }}>
                      Handoff Requested
                    </span>
                    <div style={{ flex: 1, borderTop: '1px solid #fde68a' }}></div>
                  </div>
                )}

                {sessionStatus === 'resolved' && (
                  <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0', width: '100%' }}>
                    <div style={{ flex: 1, borderTop: '1px solid #e2e8f0' }}></div>
                    <span style={{ margin: '0 16px', padding: '6px 16px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderRadius: '999px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textTransform: 'uppercase' }}>
                      Conversation Resolved
                    </span>
                    <div style={{ flex: 1, borderTop: '1px solid #e2e8f0' }}></div>
                  </div>
                )}

                {(isLoading && agentStatus) && (
                  <div className="animate-fadeIn" style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        role="presentation"
                        aria-hidden="true"
                        onMouseDown={(e) => e.preventDefault()}
                        onDragStart={(e) => { e.preventDefault(); return false; }}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '12px',
                          background: '#ffffff url(/default.png) center center / contain no-repeat',
                          flexShrink: 0,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                          border: '1px solid #f1f2f4',
                          pointerEvents: 'none',
                          userSelect: 'none',
                          WebkitUserSelect: 'none'
                        }}
                      />
                      <div style={{
                        padding: '4px 0',
                        color: '#64748b',
                        fontWeight: '500',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <span className="animate-pulse" style={{ color: '#a855f7', fontSize: '10px' }}>●</span>
                        <span
                          style={{
                            background: 'linear-gradient(90deg, #64748b 0%, #a855f7 50%, #ec4899 100%)',
                            backgroundSize: '200% auto',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent',
                            animation: 'shimmer-text 2s linear infinite',
                            display: 'inline-block',
                            fontWeight: 400
                          }}
                        >
                          {agentStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Unified Floating Input Dock */}
          <div
            ref={dockRef}
            className="copilot-input-dock"
            style={{
              padding: '10px 28px 24px 28px',
              position: 'relative',
              zIndex: 10,
              maxWidth: '820px',
              width: '100%',
              margin: '0 auto',
              transform: (messages.length === 0 && !isFirstSubmitting)
                ? `translateY(${dockOffset}px)`
                : 'translateY(0)',
              transition: isFirstSubmitting
                ? 'transform 0.32s cubic-bezier(0.16, 1, 0.3, 1)'
                : 'none'
            }}
          >
              {/* Quick Reply Choice Buttons */}
              {activeButtons.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {activeButtons.map((btn, idx) => (
                    <button
                      key={idx}
                      onClick={async () => {
                        const userMessage = {
                          id: Date.now(),
                          text: btn.label,
                          sender: 'user',
                          timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })
                        };
                        setMessages(prev => [...prev, userMessage]);
                        setActiveButtons([]);
                        saveEasyFlowMessage('USER', btn.label);
                        flowVariablesRef.current['lastClickedButton'] = btn.label;
                        flowStatusRef.current = 'running';
                        const nextId = getEdgeByHandle(flowCurrentNodeIdRef.current, btn.id) || getFirstEdge(flowCurrentNodeIdRef.current);
                        if (nextId) await executeBotFlowNode(nextId);
                        else {
                          flowStatusRef.current = 'completed';
                          appendSystemMessage('End of Flow');
                        }
                      }}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e4e4e7',
                        borderRadius: '9999px',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#18181b',
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                        transition: 'all 0.2s ease',
                        outline: 'none'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f4f4f5')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Attachment Preview Chip */}
              {selectedImage && (
                <div style={{
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: 'flex-start'
                }}>
                  <div style={{
                    background: '#18181b',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      background: '#27272a'
                    }}>
                      <img
                        src={selectedImage}
                        alt="Preview"
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          pointerEvents: 'none',
                          userSelect: 'none',
                          WebkitUserDrag: 'none'
                        }}
                      />
                    </div>
                    <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedFileName}
                    </span>
                    <button
                      onClick={removeAttachment}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'rgba(255,255,255,0.6)',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* Rounded Box Card Input Dock */}
              <div style={{
                background: '#ffffff',
                border: '1px solid #e4e4e7',
                borderRadius: '24px',
                padding: '14px 18px 12px 18px',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />

                {/* Text Input Row */}
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && sessionStatus !== 'resolved' && !isExpired) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  onPaste={handlePaste}
                  placeholder={sessionStatus === 'resolved' ? "Session resolved. Start a new chat to continue." : isExpired ? "Chat session expired after 30 mins." : "Ask me anything..."}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    fontSize: '0.96rem',
                    fontWeight: 450,
                    color: (sessionStatus === 'resolved' || isExpired) ? '#a1a1aa' : '#18181b',
                    resize: 'none',
                    minHeight: '28px',
                    maxHeight: '220px',
                    lineHeight: '1.45',
                    fontFamily: 'inherit',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin'
                  }}
                  rows={1}
                  disabled={sessionStatus === 'resolved' || isExpired}
                />

                {/* Action Toolbar Row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* Left: Attachment */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#71717a',
                        padding: '4px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title="Attach File or Image"
                    >
                      <Paperclip size={16} />
                    </button>
                  </div>

                  {/* Right: Send */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={sendMessage}
                      disabled={(!inputMessage.trim() && !selectedImage) || isLoading || sessionStatus === 'resolved' || isExpired}
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '50%',
                        background: (!inputMessage.trim() && !selectedImage) ? '#f4f4f5' : '#9333ea',
                        color: (!inputMessage.trim() && !selectedImage) ? '#a1a1aa' : '#ffffff',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: (!inputMessage.trim() && !selectedImage) ? 'not-allowed' : 'pointer',
                        boxShadow: (!inputMessage.trim() && !selectedImage) ? 'none' : '0 4px 14px rgba(147, 51, 234, 0.35)',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <Send size={15} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Privacy disclaimer */}
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <span style={{ fontSize: '0.72rem', color: '#a1a1aa' }}>
                  We respect your privacy. Your data stays protected.
                </span>
              </div>
            </div>
        </div>
      </div>


      {/* Feedback Modal Overlay */}
      {showFeedbackModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff',
            padding: '40px',
            borderRadius: '28px',
            width: '90%',
            maxWidth: '440px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            border: '1px solid #f3f4f6'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div flex="1">
                <h2 style={{ margin: 0, color: '#111827', fontSize: '20px', fontWeight: '800', letterSpacing: '-0.02em', textTransform: 'uppercase' }}>End Session</h2>
                <div style={{ height: '2px', width: '24px', background: '#111827', marginTop: '4px' }}></div>
              </div>
              <button
                onClick={() => setShowFeedbackModal(false)}
                style={{ background: '#f9fafb', border: '1px solid #f3f4f6', cursor: 'pointer', color: '#9ca3af', padding: '8px', borderRadius: '10px', transition: 'all 0.2s ease' }}
                onMouseOver={(e) => { e.currentTarget.style.color = '#111827'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                onMouseOut={(e) => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.borderColor = '#f3f4f6'; }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ margin: 0, color: '#666' }}>How would you rate your experience with our AI Assistant?</p>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: (hoverRating || rating) >= star ? '#FFD700' : '#E0E0E0',
                    transition: 'color 0.2s ease',
                    transform: (hoverRating || rating) >= star ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  <Star size={36} fill={(hoverRating || rating) >= star ? '#FFD700' : 'none'} />
                </button>
              ))}
            </div>

            <textarea
              placeholder="Tell us what went well, or what could be improved..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              style={{
                width: '100%',
                padding: '15px',
                borderRadius: '12px',
                border: '1px solid #ddd',
                minHeight: '100px',
                resize: 'none',
                fontFamily: 'inherit',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />

            <div className="flex justify-center mt-2">
              <button
                onClick={handleFeedbackSubmit}
                disabled={rating === 0}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '16px',
                  background: rating === 0 ? '#f3f4f6' : '#111827',
                  color: rating === 0 ? '#9ca3af' : '#ffffff',
                  border: 'none',
                  fontSize: '11px',
                  fontWeight: '900',
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  cursor: rating === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: rating === 0 ? 'none' : '0 10px 25px rgba(0,0,0,0.1)'
                }}
                onMouseOver={(e) => {
                  if (rating !== 0) {
                    e.currentTarget.style.background = '#000000';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = rating === 0 ? '#f3f4f6' : '#111827';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Terminate Link & Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeleteSession}
        isLoading={isDeletingSession}
        title="Delete this Chat?"
        message="This action will permanently delete this conversation history. Are you sure you want to proceed?"
      />

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes textStream {
          from { opacity: 0.5; filter: blur(2px); }
          to { opacity: 1; filter: blur(0); }
        }
        .animate-textStream {
          animation: textStream 0.3s ease-out forwards;
        }
        @keyframes neuralSpark {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-neuralSpark {
          background: linear-gradient(90deg, #f9fafb 0%, #ffffff 50%, #f9fafb 100%);
          background-size: 200% 100%;
          animation: neuralSpark 2s linear infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.9); }
        }
        .animate-pulse-custom {
          animation: pulse 1.5s ease-in-out infinite;
        }
        @keyframes shimmer-text {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .message-group:hover .message-timestamp {
          opacity: 0.8 !important;
        }
      `}</style>
    </div>
  );
};

export default ChatInterface;